import { useEffect, useState } from "react";
import { Button } from "@legends/ui";
import CavePublicScreen from "./CavePublicScreen";
import LegendCave from "./LegendCave";
import { fetchLegend, logout, type Legend } from "./api";
import { LegendsBrand, monoLabelStyle } from "./sections";

// A legend's profile page at the apex (ADR 0020) for legends whose Cave isn't a
// live storefront. Renders the same Legend Cave layout minus commerce (no
// tickets → no pass CTA / tickets panel). Cave-linked legends route to
// CavePublicScreen instead, which carries the full buy/watch flow.

export interface LegendProfileScreenProps {
  legendId: string;
  onBack: () => void;
  // Optional ticket id carries a pending purchase through sign-in (ADR 0103) for
  // the cave-linked legend path, which renders CavePublicScreen's buy flow.
  onSignIn?: (buyTicketTypeId?: string) => void;
  onLogout?: () => void;
}

const noop = () => {};

export default function LegendProfileScreen({ legendId, onBack, onSignIn, onLogout }: LegendProfileScreenProps) {
  const [legend, setLegend] = useState<Legend | null>(null);
  const [missing, setMissing] = useState(false);

  useEffect(() => {
    let active = true;
    fetchLegend(legendId)
      .then((l) => active && setLegend(l))
      .catch(() => active && setMissing(true));
    return () => {
      active = false;
    };
  }, [legendId]);

  // A legend with a live cave → the full storefront (buy/watch) for that cave.
  if (legend?.caveId) {
    return (
      <CavePublicScreen
        name={legend.name}
        caveId={legend.caveId}
        onBack={onBack}
        onSignIn={onSignIn}
        onLogout={onLogout}
      />
    );
  }

  return (
    <main style={{ maxWidth: 1120, margin: "0 auto", padding: "18px clamp(14px,3vw,28px) 40px", minHeight: "calc(100vh / var(--ui-zoom, 1))" }}>
      <header style={{ display: "flex", alignItems: "center", gap: 12, padding: "6px 0 16px" }}>
        <LegendsBrand />
        <div style={{ marginLeft: "auto", display: "flex", gap: 10 }}>
          <Button size="sm" variant="muted" onClick={onBack} aria-label="Back to legends">
            ◂ Legends
          </Button>
          {onLogout ? (
            <Button size="sm" variant="muted" onClick={() => logout().finally(() => onLogout())}>
              Sign out
            </Button>
          ) : onSignIn ? (
            <button onClick={() => onSignIn()} style={{ background: "none", border: "none", cursor: "pointer", ...monoLabelStyle, color: "var(--gold)" }}>
              Sign in ▸
            </button>
          ) : null}
        </div>
      </header>

      {missing ? (
        <p role="alert" style={{ ...monoLabelStyle, color: "var(--alert-2)" }}>Unknown legend.</p>
      ) : !legend ? (
        <p style={{ fontSize: "var(--fs-body)", color: "var(--text-dim)" }}>Loading…</p>
      ) : legend.profile ? (
        <LegendCave
          legend={legend}
          tickets={[]}
          live={[]}
          owned={new Set()}
          acquired={new Set()}
          signedIn={null}
          busyId={null}
          watching={null}
          onBuy={noop}
          onWatch={noop}
          onStopWatch={noop}
          onSignIn={onSignIn}
        />
      ) : (
        // Roster-only legend — a minimal but real hero (no fabricated content).
        <div
          style={{
            padding: "28px 24px",
            borderRadius: "var(--radius-lg)",
            border: "1px solid var(--border-mute)",
            background: "var(--surface-raised)",
            boxShadow: "var(--shadow-raised)",
          }}
        >
          <h1
            style={{
              margin: 0,
              fontFamily: "var(--font-display)",
              fontSize: "clamp(28px, 4vw, 40px)",
              fontWeight: 700,
              textTransform: "uppercase",
              color: "var(--text-hi)",
            }}
          >
            {legend.name}
          </h1>
          <p style={{ margin: "8px 0 0", fontSize: "var(--fs-body)", color: "var(--text-prose)" }}>
            {legend.position} · {legend.team} · {legend.years}
          </p>
        </div>
      )}
    </main>
  );
}
